(ns common.schema.entity-creator.copy-config
  "Pure helpers for copying subtrees of `:pipeline/creator-config` from one
   pipeline into another. Originally lifted from
   `migration.mig-2026-06-16.copy-creation-config`, kept structurally identical
   so REPL/migration usage and UI usage share a single implementation."
  (:require
   [clojure.set :as set]
   [common.data-types :as data-types]
   [common.permissions.field-permissions :as field-permissions]
   [common.schema.entities.pipeline :as c-pipeline]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.registry :as registry]
   [common.util :as cu]))

(def all-slots
  #{:single-entity-config
    :associations-config
    :main-config
    :target-config
    :target-profile-config})

(defn target-typed-path?
  [path]
  (= 3 (count path)))

(defn path-slot
  [path]
  (last path))

(defn path-entity-type
  [path]
  (first path))

(defn path-target-type
  [path]
  (when (target-typed-path? path)
    (second path)))

(defn stage-name-attribute
  [pipeline]
  (-> pipeline
      :pipeline/entity-type
      c-pipeline/entity-type-mappings
      :stage-name-attribute))

(defn stage-pipeline-attribute
  [pipeline]
  (-> pipeline
      :pipeline/entity-type
      c-pipeline/entity-type-mappings
      :stage-pipeline-attribute))

(defn build-stage-id-map
  "Returns {:stage-map {src-id tgt-id ...}
            :name-matches integer
            :unmapped-src-stage-names [...]
            :unmapped-tgt-stage-names [...]
            :fallback-used? bool}.

  Names matched case-insensitively after trim. If no name matches, falls back
  to {first-src first-tgt} (when both exist)."
  [{:keys [src-stages tgt-stages src-name-attr tgt-name-attr]}]
  (let [norm (fn [s]
               (some-> s cu/str-trim-lcase))
        tgt-by-name (->> tgt-stages
                         (map (fn [s] [(norm (get s tgt-name-attr)) (:db/id s)]))
                         (filter first)
                         (into {}))
        src-pairs (->> src-stages
                       (map (fn [s]
                              {:src-id (:db/id s)
                               :src-name (get s src-name-attr)
                               :tgt-id (get tgt-by-name (norm (get s src-name-attr)))})))
        matched (->> src-pairs
                     (filter :tgt-id))
        unmapped-src (->> src-pairs
                          (remove :tgt-id)
                          (mapv :src-name))
        matched-tgt-ids (->> matched (map :tgt-id) set)
        unmapped-tgt (->> tgt-stages
                          (remove (fn [s] (contains? matched-tgt-ids (:db/id s))))
                          (mapv #(get % tgt-name-attr)))
        fallback? (and (empty? matched)
                       (seq src-stages)
                       (seq tgt-stages))
        stage-map (cond
                    (seq matched)
                    (->> matched (map (juxt :src-id :tgt-id)) (into {}))
                    fallback?
                    {(:db/id (first src-stages)) (:db/id (first tgt-stages))}
                    :else {})]
    {:stage-map stage-map
     :name-matches (count matched)
     :unmapped-src-stage-names unmapped-src
     :unmapped-tgt-stage-names unmapped-tgt
     :fallback-used? fallback?}))

(defn translate-stage-keyed-map
  "Translate {src-stage-id -> v} to {tgt-stage-id -> v}, dropping unmapped."
  [stage-map m]
  (->> m
       (keep (fn [[src-id v]]
               (when-let [tgt-id (get stage-map src-id)]
                 [tgt-id v])))
       (into {})))

(defn translate-field-config
  [{:keys [stage-map keep-field?]} field-entity-type field-config]
  (when (or (nil? keep-field?)
            (nil? field-entity-type)
            (keep-field? field-entity-type (:attribute field-config)))
    (cond-> field-config
      (map? (:stages field-config))
      (update :stages #(translate-stage-keyed-map stage-map %)))))

(defn translate-association-config
  [{:keys [stage-map keep-association?]} association-config]
  (when (or (nil? keep-association?)
            (keep-association? association-config))
    (cond-> association-config
      (map? (:stages association-config))
      (update :stages #(translate-stage-keyed-map stage-map %)))))

(defn translate-prevent-create
  [{:keys [stage-map]} prevent-create]
  (cond-> prevent-create
    (map? (:stages prevent-create))
    (update :stages #(translate-stage-keyed-map stage-map %))))

(defn translate-sub-config
  "Translate a single config map (:main-config / :target-config /
  :target-profile-config / :associations-config / :single-entity-config).
  `field-entity-type` is the entity-type that OWNS the fields inside this
  slot's config (used to look up per-field in-use config) - not the path's
  outer entity-type."
  [opts field-entity-type sub-config]
  (cond-> sub-config
    (vector? (:fields sub-config))
    (update :fields
            (fn [fields]
              (->> fields
                   (keep #(translate-field-config opts field-entity-type %))
                   vec)))
    (vector? (:associations sub-config))
    (update :associations
            (fn [associations]
              (->> associations
                   (keep #(translate-association-config opts %))
                   vec)))
    (map? (:prevent-create? sub-config))
    (update :prevent-create? #(translate-prevent-create opts %))))

(defn field-entity-type-for-path
  "Returns the entity-type that owns the fields inside the slot at the end
  of `path`. Used to look up per-field in-use configuration, which is keyed
  by the field's owning entity-type (not the path's outer entity-type)."
  [{:keys [path pipeline]}]
  (let [slot (path-slot path)
        entity-type (path-entity-type path)
        target-type (path-target-type path)]
    (case slot
      :main-config entity-type
      :single-entity-config entity-type
      :target-config target-type
      :target-profile-config (ecu/resolve-target-profile-type
                              {:pipeline pipeline
                               :entity-type entity-type
                               :target-type target-type})
      :associations-config nil
      nil)))

(defn enumerate-source-paths
  "Walk src creator-config and return all leaf config paths it contains."
  [src-config]
  (->> src-config
       (mapcat
        (fn [[entity-type subtree]]
          (when (map? subtree)
            (->> subtree
                 (mapcat
                  (fn [[k v]]
                    (cond
                      (contains? all-slots k)
                      [[entity-type k]]
                      (and (keyword? k) (map? v))
                      (->> v
                           (keys)
                           (filter all-slots)
                           (mapv (fn [slot] [entity-type k slot])))
                      :else nil)))))))
       vec))

(def investor-legal-entity-flavors
  #{:entity.type/company :entity.type/person})

(defn uses-investor-target-profile?
  "True when the pipeline's flow uses an investor target-profile - i.e. the
  user selects a company/person as the legal-entity flavor of an investor
  target, and the inner target-type keys in creator-config are legal-entity
  flavors rather than the pipeline's :pipeline/target-asset-types."
  [pipeline]
  (let [entity-type (:pipeline/entity-type pipeline)
        target-asset-types (set (:pipeline/target-asset-types pipeline))]
    (or (= entity-type :entity.type/fundraising-opportunity)
        (= entity-type :entity.type/exit-opportunity)
        (and (= entity-type :entity.type/sales-opportunity)
             (= target-asset-types #{:entity.type/investor})))))

(defn allowed-inner-target-types
  [pipeline]
  (if (uses-investor-target-profile? pipeline)
    investor-legal-entity-flavors
    (set (:pipeline/target-asset-types pipeline))))

(defn applicable-path?
  "Decide whether `path` from source can be applied to `tgt-pipeline`.
  Returns nil when applicable, else a keyword skip-reason."
  [{:keys [path tgt-pipeline]}]
  (let [entity-type (path-entity-type path)
        target-type (path-target-type path)
        slot (path-slot path)
        tgt-entity-type (:pipeline/entity-type tgt-pipeline)
        allow-without-target? (boolean (:pipeline/allow-without-target? tgt-pipeline))
        tgt-allowed-inner (allowed-inner-target-types tgt-pipeline)]
    (cond
      (and target-type
           (not (contains? tgt-allowed-inner target-type)))
      :target-type-not-supported

      (and (= slot :target-profile-config)
           (not (uses-investor-target-profile? tgt-pipeline)))
      :no-target-profile-on-target

      (and (nil? target-type)
           (= entity-type tgt-entity-type)
           (not allow-without-target?)
           (contains? #{:single-entity-config :associations-config} slot))
      :target-required-on-target-pipeline

      :else nil)))

(defn path-present-in-source?
  [src-config path]
  (some? (get-in src-config path)))

(defn assoc-by-path
  [m path v]
  (assoc-in m path v))

(defn target-profile-types-for-pipeline
  [pipeline]
  (->> (:pipeline/target-asset-types pipeline)
       (keep (fn [tt]
               (ecu/resolve-target-profile-type
                {:pipeline pipeline
                 :entity-type (:pipeline/entity-type pipeline)
                 :target-type tt})))
       set))

(defn association-source-entity-type
  [association-config]
  (some-> association-config :association-key first))

(defn make-keep-association?
  "Build a default association predicate that drops associations whose
  source-entity-type is a target-profile type reachable from src but not tgt."
  [{:keys [src-pipeline tgt-pipeline]}]
  (let [src-profile-types (target-profile-types-for-pipeline src-pipeline)
        tgt-profile-types (target-profile-types-for-pipeline tgt-pipeline)
        unreachable (set/difference src-profile-types tgt-profile-types)]
    (fn [association-config]
      (let [src-et (association-source-entity-type association-config)]
        (not (contains? unreachable src-et))))))

(defn in-use-value-for-attribute
  [{:keys [tenant-field-configurations pipeline-settings field-schema
           entity-type attribute]}]
  (or (when (= :mandatory/always (:field/mandatory field-schema))
        :in-use-disabled)
      (get-in pipeline-settings
              [entity-type attribute :field-settings/in-use-config])
      (get-in tenant-field-configurations
              [entity-type attribute :field-settings/in-use-config])
      (get-in field-schema [:field/default-visibility :field-visibility])))

(defn make-keep-field?
  "Build a default field predicate that drops fields whose effective in-use
  config on target resolves to a NEGATIVE-IN-USE-VALUE."
  [{:keys [tgt-pipeline]}]
  (let [tenant (:common/tenant tgt-pipeline)
        tenant-cfg (some-> tenant
                           :tenant/field-configurations
                           data-types/parse-edn-string)
        pipeline-cfg (some-> tgt-pipeline
                             :pipeline/settings
                             data-types/parse-edn-string)
        tenant-schema (registry/cross-entity-schema tenant)
        entity-schema-for (memoize
                           (fn [entity-type]
                             (registry/get-schema tenant-schema entity-type)))]
    (fn [entity-type attribute]
      (let [field-schema (get (entity-schema-for entity-type) attribute)
            v (in-use-value-for-attribute
               {:tenant-field-configurations tenant-cfg
                :pipeline-settings pipeline-cfg
                :field-schema field-schema
                :entity-type entity-type
                :attribute attribute})]
        (not (contains? field-permissions/NEGATIVE-IN-USE-VALUES v))))))

(defn coerce-predicate
  "Coerce a predicate-shaped argument. nil -> (default-thunk); true -> keep-all;
  false -> drop-all; fn -> as-is."
  [v default-thunk]
  (cond
    (nil? v) (default-thunk)
    (true? v) (constantly true)
    (false? v) (constantly false)
    (fn? v) v
    :else (throw (ex-info "Predicate must be nil, boolean, or fn" {:value v}))))

(defn compute-new-creator-config
  "Pure. Returns {:tgt-config new-config :report {...}}."
  [{:keys [src-config tgt-config paths stage-map src-pipeline tgt-pipeline
           keep-field? keep-association?]}]
  (let [keep-field? (coerce-predicate
                     keep-field?
                     #(make-keep-field? {:tgt-pipeline tgt-pipeline}))
        keep-association? (coerce-predicate
                           keep-association?
                           #(make-keep-association?
                             {:src-pipeline src-pipeline
                              :tgt-pipeline tgt-pipeline}))
        translate-opts {:stage-map stage-map
                        :keep-field? keep-field?
                        :keep-association? keep-association?}
        init {:config (or tgt-config {})
              :applied []
              :skipped []}
        result
        (->> paths
             (reduce
              (fn [acc path]
                (let [reason (applicable-path? {:path path :tgt-pipeline tgt-pipeline})]
                  (cond
                    reason
                    (update acc :skipped conj {:path path :reason reason})

                    (not (path-present-in-source? src-config path))
                    (update acc :skipped conj {:path path :reason :missing-in-source})

                    :else
                    (let [src-value (get-in src-config path)
                          field-entity-type (field-entity-type-for-path
                                             {:path path
                                              :pipeline src-pipeline})
                          translated (translate-sub-config translate-opts
                                                           field-entity-type
                                                           src-value)]
                      (-> acc
                          (update :config assoc-by-path path translated)
                          (update :applied conj path))))))
              init))]
    {:tgt-config (:config result)
     :report (-> result
                 (dissoc :config))}))
